import { gql, useMutation, type FetchResult } from "@apollo/client";

import type {
  GetServicesByNodeQuery,
  Service,
  SetIsRunningServiceStatusMutation,
  SetIsRunningServiceStatusMutationVariables,
  SetServiceStatusInput,
} from "../../../API";
import { SET_RUNNING_SERVICE_STATUS } from "../../../common/operations/mutations";
import { GET_SERVICES_BY_NODE } from "../../../common/operations/queries";
import { successNotification } from "../../../common/variables/notification";
import { servicesByNodeVariable } from "../variables/devices";
import { handleApolloError } from "../../../common/utils/handleApolloError";

interface UpdateServiceInterface {
  setIsRunningServiceStatus: (
    input: SetServiceStatusInput,
    notificationMessage: string
  ) => Promise<FetchResult<SetIsRunningServiceStatusMutation>>;
  data?: SetIsRunningServiceStatusMutation | null;
  loading: boolean;
}

export const useSetIsRunningServiceStatus = (): UpdateServiceInterface => {
  const [updateStatus, { data, loading }] = useMutation<
    SetIsRunningServiceStatusMutation,
    SetIsRunningServiceStatusMutationVariables
  >(SET_RUNNING_SERVICE_STATUS);

  const setIsRunningServiceStatus = async (
    input: SetServiceStatusInput,
    notificationMessage: string
  ): Promise<FetchResult<SetIsRunningServiceStatusMutation>> => {
    const nodeId = input.nodeId;

    return await updateStatus({
      variables: {
        input,
      },
      onError: handleApolloError,
      update(cache, { data }) {
        if (data && data.setIsRunningServiceStatus) {
          const queryData = cache.readQuery<GetServicesByNodeQuery>({
            query: GET_SERVICES_BY_NODE,
            variables: { nodeId },
          });

          if (queryData) {
            const updatedServices =
              queryData.getServicesByNode.items.map(service => {
                if (
                  (data.setIsRunningServiceStatus.id ?? "").includes(
                    service?.id as string
                  ) &&
                  service?.deviceId === data.setIsRunningServiceStatus.deviceId
                ) {
                  return {
                    ...service,
                    isRunning: data.setIsRunningServiceStatus.isRunning,
                  };
                }

                return service;
              }) ?? [];

            cache.writeQuery({
              query: GET_SERVICES_BY_NODE,
              data: {
                getServicesByNode: {
                  ...queryData.getServicesByNode,
                  items: updatedServices,
                },
              },
            });

            servicesByNodeVariable(updatedServices as Service[]);
          }

          cache.modify({
            fields: {
              getServicesByDevice(existing) {
                const newServiceRef = cache.writeFragment({
                  data: data.setIsRunningServiceStatus,
                  fragment: gql`
                    fragment NewService on Service {
                      configuration
                      customerId
                      deviceId
                      healthData
                      id
                      isRunning
                      nodeId
                      serviceType
                    }
                  `,
                });

                return {
                  ...existing,
                  items: existing.items.map((item: Service) => {
                    if (item?.id !== data.setIsRunningServiceStatus.id) {
                      return item;
                    }

                    return newServiceRef;
                  }),
                };
              },
            },
          });
        }

        successNotification(notificationMessage);
      },
    });
  };

  return { setIsRunningServiceStatus, data, loading };
};
